/**
 * AuthSplitLayout.tsx — SK Platform
 *
 * The two-panel auth shell: a solid BLACK brand panel on the left, the form on
 * the right. Shared by sign-in and sign-up so both wear the same frame — the
 * two screens sit one click apart, and a layout that changes between them reads
 * as landing on a different site.
 *
 * ── IT COLLAPSES ON NARROW SCREENS, AND THAT IS THE POINT ─────────────────
 *
 * Below the `mobile` breakpoint (820px) the black panel is not rendered at all
 * — not shrunk, not stacked above the form. On a phone, vertical space is the
 * scarce resource on a form this long, and a decorative panel above it would
 * push the first field below the fold. So the panel is desktop/tablet only, and
 * on mobile the form takes the whole screen.
 *
 * That is also why the panel owns the brand mark on wide screens: the form card
 * would otherwise show its own AuthBrandHeader beside a panel already saying
 * SOFTKNACK. Callers therefore render their header only when the panel is
 * absent — `useAuthPanelVisible()` is exported so both screens ask the same
 * question rather than each re-deriving the breakpoint.
 *
 * The panel is deliberately plain: brand, a warm line for the screen (welcome
 * on sign-up, welcome back on sign-in) and a few shared points, nothing that
 * needs maintaining. It is a backdrop for a form, not a landing page — anything
 * with a product list or pricing in it goes stale silently, because nobody
 * revisits the sign-in screen when a product ships.
 */

import React from 'react';
import { View, Text, StyleSheet, ScrollView, Platform, KeyboardAvoidingView } from 'react-native';

import { useTheme } from '../context/themeContext';
import { useResponsive } from '../utils/responsive';

/** The panel's default positioning line — the tagline the form card used to
 *  carry in a pill. It reads better here, where there is room for it. */
/** Near-black rather than #000 — a true black panel beside a white form edge
 *  is harsher than it looks in a mockup. */
const PANEL_BG = '#0B0B0D';

const DEFAULT_HEADLINE = 'AI products for your entire business';
const DEFAULT_SUB =
  'One account for every Softknack product — sales, support, hiring, billing and more, in one place.';

/**
 * Whether the brand panel is on screen. Callers use it to decide whether their
 * card still needs its own brand header, so the mark never appears twice.
 */
export function useAuthPanelVisible(): boolean {
  const { isMobile } = useResponsive();
  return !isMobile;
}

export interface AuthSplitLayoutProps {
  /** The form. Rendered on the right on wide screens, full-width on mobile. */
  readonly children: React.ReactNode;
  readonly headline?: string;
  readonly sub?: string;
  /**
   * A few short lines on what Softknack does for the reader, each with a tick.
   * Kept to what every product shares (one account, the AI receptionist, your
   * region) so they do not go stale when a single product changes.
   */
  readonly points?: readonly string[];
}

const AuthSplitLayout: React.FC<AuthSplitLayoutProps> = ({
  children,
  headline = DEFAULT_HEADLINE,
  sub = DEFAULT_SUB,
  points = [],
}) => {
  const { theme } = useTheme();
  const { colors } = theme;
  const showPanel = useAuthPanelVisible();

  return (
    <View style={[s.root, { backgroundColor: colors.paper }]}>
      {showPanel ? (
        <View
          style={[
            s.panel,
            // Fixed near-black, not a palette token: this panel is dark in
            // BOTH themes, so its colours belong to it rather than tracking a
            // theme that would invert them.
            { backgroundColor: PANEL_BG },
            Platform.OS === 'web'
              ? ({
                  // A near-black wash rather than a flat fill, so the panel has
                  // some depth behind the wordmark without becoming a graphic.
                  backgroundImage:
                    'linear-gradient(160deg, #15161A 0%, #0B0B0D 55%, #000000 100%)',
                } as any)
              : {},
          ]}
        >
          <View style={s.panelInner}>
            {/* Brand — the tile inverts on black: white chip, dark letters. */}
            <View style={s.brandRow}>
              <View style={[s.logoMark, { backgroundColor: colors.white }]}>
                <Text style={[s.logoText, { color: '#0B0B0D' }]}>SK</Text>
              </View>
              <Text style={[s.logoName, { color: colors.white }]}>SOFTKNACK</Text>
            </View>

            <Text style={[s.headline, { color: colors.white }]}>{headline}</Text>
            <Text style={s.sub}>{sub}</Text>

            {points.length > 0 ? (
              <View style={s.points}>
                {points.map((p) => (
                  <View key={p} style={s.pointRow}>
                    <View style={s.tick}>
                      <Text style={s.tickTxt}>✓</Text>
                    </View>
                    <Text style={s.pointTxt}>{p}</Text>
                  </View>
                ))}
              </View>
            ) : null}

            {/* A hairline rule instead of a decorative graphic — it closes the
                block without adding anything that can look dated. */}
            <View style={s.rule} />
            <Text style={s.foot}>© Softknack</Text>
          </View>
        </View>
      ) : null}

      <KeyboardAvoidingView
        style={s.formSide}
        // Lifted from the login screen, which had it while signup did not —
        // the taller form is the one that needed it more.
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          contentContainerStyle={s.formScroll}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {children}
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const s = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: 'row',
    ...(Platform.OS === 'web' ? ({ minHeight: '100vh' } as any) : {}),
  },
  // Slightly narrower than the form side: the form is the job, the panel is the
  // frame around it.
  panel: {
    flex: 0.9,
    justifyContent: 'center',
    paddingHorizontal: 56,
    ...(Platform.OS === 'web' ? ({ minHeight: '100vh' } as any) : {}),
  },
  panelInner: { maxWidth: 420 },
  brandRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 40 },
  logoMark: {
    width: 42,
    height: 42,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoText: {
    fontWeight: '900',
    fontSize: 16,
    fontFamily: Platform.OS === 'web' ? 'IBM Plex Mono, monospace' : undefined,
    letterSpacing: 0.3,
  },
  logoName: {
    fontSize: 23,
    fontWeight: '800',
    letterSpacing: 3.2,
    lineHeight: 26,
    // Cancels the trailing letter-spacing so the mark sits where it looks like
    // it should — same correction as AuthBrandHeader.
    marginRight: -3.2,
  },
  headline: {
    fontSize: 36,
    fontWeight: '900',
    letterSpacing: -1,
    lineHeight: 44,
    marginBottom: 16,
  },
  sub: {
    fontSize: 14.5,
    fontWeight: '500',
    lineHeight: 24,
    // Muted white rather than a theme ink: this panel is always dark, in both
    // themes, so its text colours are fixed to it and not to the palette.
    color: 'rgba(255,255,255,0.62)',
  },
  points: { marginTop: 28, gap: 14 },
  pointRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
  tick: {
    width: 22,
    height: 22,
    borderRadius: 11,
    marginTop: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.10)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
  },
  tickTxt: { color: '#7CE0A6', fontSize: 12, fontWeight: '800', lineHeight: 14 },
  pointTxt: { flex: 1, fontSize: 14, fontWeight: '600', lineHeight: 22, color: 'rgba(255,255,255,0.86)' },
  rule: {
    height: 1,
    marginTop: 40,
    marginBottom: 16,
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  foot: { fontSize: 12, fontWeight: '600', color: 'rgba(255,255,255,0.38)', letterSpacing: 0.3 },

  formSide: { flex: 1.1 },
  formScroll: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    /**
     * Asymmetric on purpose — the card sits HIGHER than dead centre.
     *
     * The signup form is taller than most viewports, and centred content that
     * overflows gets clipped at BOTH ends equally: the heading disappears off
     * the top while the sign-in footer disappears off the bottom, and the
     * reader has to scroll up to discover what they are even filling in. A
     * smaller top pad than bottom pad biases the card upward, so the heading
     * and first fields are what you land on.
     *
     * It works for the short cards too: with room to spare, unequal padding
     * simply shifts the centred card up by half the difference (12px here) —
     * a nudge, not a different layout for login and signup.
     *
     * Keep the top value clear of single digits: below ~16 the card sits flush
     * against the browser chrome with no air above the heading.
     */
    paddingTop: 24,
    paddingBottom: 48,
    paddingHorizontal: 20,
  },
});

export default AuthSplitLayout;
